import { db } from "../../main";
import { getClientRef } from "../../data/repository/ClientRepository";
import {
  DryingProjectOverview,
  getProjectOverviewEndDate,
} from "../../data/models/DryingProjectOverview";
import LeakDetectionProjectOverview from "../../data/models/LeakDetectionProjectOverview";

import { firestore } from "firebase/app";
import { ProjectsModule } from "@/store/modules/ProjectsModule";
import {
  ProjectDetails,
  containsLeakDetectionInfo,
  ApprovalResult,
  isFlexProject,
  containsOnlyLeakDetectionInfo,
  isProjectWebsiteOnly,
} from "@/data/models/ProjectDetails";
import { toStartOfDay } from "@/utils/DateHelper";
import {
  DryingProjectOverviewPage,
  mapPageDocumentToOverviewPage,
  mergeDryingProjectOverviewPages,
} from "@/utils/ProjectOverviewPages";

interface DryingProjectsObserverParameters {
  ref: firestore.Query;
  onNext: (DryingProjectOverviews: Array<DryingProjectOverview>) => void;
  filter?: (DryingProjectOverview: DryingProjectOverview) => boolean;
}

interface LeakDetectionProjectsObserverParameters {
  ref: firestore.Query;
  onNext: (
    LeakDetectionProjectOverviews: Array<LeakDetectionProjectOverview>
  ) => void;
  filter?: (
    LeakDetectionProjectOverview: LeakDetectionProjectOverview
  ) => boolean;
}

export interface ProjectOverviewOptions {
  forcedPageNumber?: number;
  isProjectFinished?: boolean;
}

export function getProjectOverviewRef(
  projectDetails: ProjectDetails,
  options?: ProjectOverviewOptions
) {
  const internalProjectNumber = projectDetails.internalProjectNumber;
  const pageNumber =
    options?.forcedPageNumber ?? Math.floor(internalProjectNumber / 100);

  let isProjectFinished = false;

  if (options?.isProjectFinished !== undefined) {
    isProjectFinished = options.isProjectFinished;
  } else if (
    projectDetails.clientData.projectApproval?.approvalResult ===
    ApprovalResult.REJECTED
  ) {
    isProjectFinished = true;
  } else if (containsLeakDetectionInfo(projectDetails)) {
    isProjectFinished = isFlexProject(projectDetails) && containsOnlyLeakDetectionInfo(projectDetails)
      ? !!projectDetails?.finishDamagePatternData?.leakDetectionCompletedBy
      : !!projectDetails?.leakDetectionConclusionData?.concludedBy;
  } else {
    const conclusionData = projectDetails.projectConclusionData;

    isProjectFinished = !!conclusionData?.projectConclusionDate && 
      (isProjectWebsiteOnly(projectDetails) || !!conclusionData?.complete);
  }

  const collection =
    !isFlexProject(projectDetails) && containsLeakDetectionInfo(projectDetails)
      ? isProjectFinished
        ? "finishedLeakDetectionProjectOverviews"
        : "runningLeakDetectionProjectOverviews"
      : isProjectFinished
      ? "finishedProjectOverviews"
      : "runningProjectOverviews";

  return `${getClientRef()}/${collection}/page_${pageNumber}`;
}

export async function updateProjectOverview(
  projectDetails: ProjectDetails,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  return db
    .doc(getProjectOverviewRef(projectDetails))
    .set(update, { merge: true });
}

export async function getProjectOverview(
  projectDetails: ProjectDetails,
  options?: ProjectOverviewOptions
) {
  const ref = getProjectOverviewRef(projectDetails, options);

  const result = await db.doc(ref).get();
  const data = result.get("data");

  return data[projectDetails.id] as DryingProjectOverview;
}

export async function deleteProjectOverview(
  projectDetails: ProjectDetails,
  options?: ProjectOverviewOptions
) {
  const ref = getProjectOverviewRef(projectDetails, options);
  const dataRef = `data.${projectDetails.id}`;

  return Promise.all([
    db.doc(ref).update({
      [dataRef]: firestore.FieldValue.delete(),
    }),
  ]);
}

export async function setProjectOverviewOfFinishedProject(
  projectDetails: ProjectDetails,
  endDate: firestore.Timestamp,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  const ref = getProjectOverviewRef(projectDetails);

  const result = await db.doc(ref).get();

  const mostRecentlyFinishedProjectDate = result.get(
    "mostRecentlyFinishedProjectDate"
  ) as firestore.Timestamp;
  const leastRecentlyFinishedProjectDate = result.get(
    "leastRecentlyFinishedProjectDate"
  ) as firestore.Timestamp;

  let mostRecently = endDate;
  let leastRecently = endDate;

  if (
    mostRecentlyFinishedProjectDate &&
    mostRecentlyFinishedProjectDate.seconds > mostRecently.seconds
  ) {
    mostRecently = mostRecentlyFinishedProjectDate;
  }

  if (
    leastRecentlyFinishedProjectDate &&
    leastRecentlyFinishedProjectDate.seconds < leastRecently.seconds
  ) {
    leastRecently = leastRecentlyFinishedProjectDate;
  }

  return db.doc(ref).set(
    {
      mostRecentlyFinishedProjectDate: mostRecently,
      leastRecentlyFinishedProjectDate: leastRecently,
      data: {
        [projectDetails.id]: update,
      },
    },
    { merge: true }
  );
}

export async function setProjectOverview(
  projectDetails: ProjectDetails,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  const ref = getProjectOverviewRef(projectDetails);

  return db.doc(ref).set(
    {
      data: {
        [projectDetails.id]: update,
      },
    },
    { merge: true }
  );
}

function mapSnapshotToDryingProjectOverviews(
  snapshot: firestore.QuerySnapshot<firestore.DocumentData>,
  filter?: (DryingProjectOverview: DryingProjectOverview) => boolean
) {
  const pages: DryingProjectOverviewPage[] = [];

  snapshot.forEach((doc) => {
    pages.push(mapPageDocumentToOverviewPage(doc, filter));
  });

  return mergeDryingProjectOverviewPages(pages);
}

export async function getRunningDryingProjectOverviews() {
  const ref = getClientRef() + "/runningProjectOverviews";

  const snapshot = await db.collection(ref).get();

  return mapSnapshotToDryingProjectOverviews(snapshot);
}

export function observeDryingProjects(
  params: DryingProjectsObserverParameters
): () => void {
  return params.ref.onSnapshot((snapshot) => {
    const dryingProjectOverviews = mapSnapshotToDryingProjectOverviews(
      snapshot
    );

    const filter = params.filter;

    if (filter) {
      params.onNext(
        dryingProjectOverviews.filter((projectOverview) =>
          filter(projectOverview)
        )
      );
    } else {
      params.onNext(dryingProjectOverviews);
    }
  });
}

export function observeLeakDetectionProjects(
  params: LeakDetectionProjectsObserverParameters
): () => void {
  return params.ref.onSnapshot((snapshot) => {
    const result: Array<LeakDetectionProjectOverview> = [];

    snapshot.forEach((doc) => {
      const data = doc.get("data");

      Object.values(data).forEach((data) => {
        const overview = data as LeakDetectionProjectOverview;

        if (overview && (!params.filter || params.filter(overview))) {
          result.push(overview);
        }
      });
    });

    params.onNext(result);
  });
}

export function observeFinishedDryingProjects() {
  const collection = getClientRef() + "/finishedProjectOverviews";
  const ref = db.collection(collection);

  return observeDryingProjects({
    ref: ref,
    onNext: (dryingProjectOverviews: Array<DryingProjectOverview>) => {
      ProjectsModule.setFinishedDryingProjects(dryingProjectOverviews);
    },
  });
}

export function observeRecentlyFinishedDryingProjects(daysThreshold: number) {
  const cutOffDate = toStartOfDay(
    firestore.Timestamp.fromDate(new Date())
  ).toDate();

  cutOffDate.setDate(cutOffDate.getDate() - daysThreshold);

  const collection = getClientRef() + "/finishedProjectOverviews";
  const ref = db
    .collection(collection)
    .where("mostRecentlyFinishedProjectDate", ">=", cutOffDate);

  return observeDryingProjects({
    ref: ref,
    onNext: (dryingProjectOverviews: Array<DryingProjectOverview>) => {
      ProjectsModule.setRecentlyFinishedDryingProjects(dryingProjectOverviews);
    },
    filter: (overview) => {
      if (overview.endDate) {
        return overview.endDate.toDate().getTime() > cutOffDate.getTime();
      } else {
        return false;
      }
    },
  });
}

export function observeRunningDryingProjects() {
  const ref = getClientRef() + "/runningProjectOverviews";

  return observeDryingProjects({
    ref: db.collection(ref),
    onNext: (dryingProjectOverviews: Array<DryingProjectOverview>) => {
      ProjectsModule.setRunningDryingProjects(dryingProjectOverviews);
    },
  });
}

export async function checkIfArchivedProjectsExist() {
  const ref = getClientRef() + "/archivedProjects";

  const querySnapshot = await db
    .collection(ref)
    .where("deleted", "!=", true)
    .limit(1)
    .get();

  // If there's at least one document, return true
  if (!querySnapshot.empty) {
    return true;
  }

  // If no projects are found in any documents
  return false;
}

export async function checkIfDeletedProjectsExist() {
  const ref = getClientRef() + "/archivedProjects";

  const querySnapshot = await db
    .collection(ref)
    .where("deleted", "==", true)
    .limit(1)
    .get();

  // If there's at least one document, return true
  if (!querySnapshot.empty) {
    return true;
  }

  // If no projects are found in any documents
  return false;
}

export function observeFinishedLeakDetectionProjects() {
  const collection = getClientRef() + "/finishedLeakDetectionProjectOverviews";
  const ref = db.collection(collection);

  return observeLeakDetectionProjects({
    ref: ref,
    onNext: (
      leakDetectionProjectOverviews: Array<LeakDetectionProjectOverview>
    ) => {
      ProjectsModule.setFinishedLeakDetectionProjects(
        leakDetectionProjectOverviews
      );
    },
  });
}

export function observeRecentlyFinishedLeakDetectionProjects(
  daysThreshold: number
) {
  const cutOffDate = new Date();

  cutOffDate.setDate(cutOffDate.getDate() - daysThreshold);

  const collection = getClientRef() + "/finishedLeakDetectionProjectOverviews";
  const ref = db
    .collection(collection)
    .where("mostRecentlyFinishedProjectDate", ">=", cutOffDate);

  return observeLeakDetectionProjects({
    ref: ref,
    onNext: (
      leakDetectionProjectOverviews: Array<LeakDetectionProjectOverview>
    ) => {
      ProjectsModule.setRecentlyFinishedLeakDetectionProjects(
        leakDetectionProjectOverviews
      );
    },
    filter: (overview) => {
      if (overview.endDate) {
        return overview.endDate.toDate().getTime() > cutOffDate.getTime();
      } else {
        return false;
      }
    },
  });
}

export function observeRunningLeakDetectionProjects() {
  const ref = getClientRef() + "/runningLeakDetectionProjectOverviews";

  return observeLeakDetectionProjects({
    ref: db.collection(ref),
    onNext: (
      leakDetectionProjectOverviews: Array<LeakDetectionProjectOverview>
    ) => {
      ProjectsModule.setRunningLeakDetectionProjects(
        leakDetectionProjectOverviews.filter(
          (overview) => !getProjectOverviewEndDate(overview)
        )
      );
    },
  });
}
